import {
  Box,
  Stack,
  Typography,
  Toolbar,
  Container,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useCurrentStore, useOrdering } from 'hooks'
import { useNavigate } from 'react-router-dom'
import { useMemo } from 'react'
import { SuccessIcon } from 'components/images'
import { getOrderSubmittedNote } from 'services'
import { Background2 } from 'components/backgrounds/Background2'
import { OrderSummary } from './OrderSummary'
import { KBottomAppBar } from 'components/core/KBottomAppBar'
import { BottomBarActionButton } from './BottomBarActionButton'
import { OrderReview } from './OrderReview'

interface SubmitOrderSuccessProps {
  backUrl: string
  showOrderDetail?: boolean
}

export const SubmitOrderSuccess = ({
  backUrl,
  showOrderDetail,
}: SubmitOrderSuccessProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { store } = useCurrentStore()
  const {
    order,
    submitOrderState: { result },
    clearOrder,
    clearSubmitOrderState,
  } = useOrdering()

  const [message, params] = useMemo(() => {
    if (!order || !result || !store) return ['', {}]
    return getOrderSubmittedNote(store, order, result)
  }, [order, result, store])

  const handleClose = () => {
    clearOrder()
    clearSubmitOrderState()
    navigate(backUrl)
  }

  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <Background2 sx={{ flexGrow: 1, overflowY: 'auto', pt: 4, pb: 4 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              borderRadius: 4,
              backgroundColor: 'background.paper',
            }}
          >
            <Stack
              sx={{
                margin: 'auto',
                gap: 2,
                alignItems: 'stretch',
                padding: 4,
                maxWidth: 560,
              }}
            >
              <Box sx={{ alignSelf: 'center' }}>
                <SuccessIcon width={isDownSm ? 120 : 160} />
              </Box>
              <Typography
                variant="h4"
                color="secondary"
                sx={{ alignSelf: 'center' }}
              >
                {t('Thanks you for your order!')}
              </Typography>

              <Typography
                variant="h4"
                color="primary"
                data-chromatic="ignore"
                sx={{ alignSelf: 'center' }}
              >
                <Trans defaults={message} values={params} />
              </Typography>

              <Divider />
              {order?.customer_email && (
                <>
                  <Stack sx={{ gap: 1 }}>
                    <Typography variant="body1" color="secondary">
                      <Trans
                        defaults="Order receipt has been sent to <strong>{{ email }}</strong>." // optional defaultValue
                        values={{ email: order.customer_email }}
                      />
                    </Typography>
                    <Typography variant="body2" color="secondary">
                      <Trans
                        defaults="Your receipt will come from <strong>kiolyn.com</strong>.<br/>Please make sure it's not in your spam folder." // optional defaultValue
                      />
                    </Typography>
                  </Stack>
                  <Divider />
                </>
              )}
              {showOrderDetail ? (
                <Box>
                  <OrderReview
                    direction="column"
                    readonly={true}
                    containerSx={{ padding: '0 !important' }}
                  />
                </Box>
              ) : (
                <>
                  <Typography variant="h3">{t('Order summary')}</Typography>
                  <OrderSummary showTip={true} />
                </>
              )}
            </Stack>
          </Box>
        </Container>
      </Background2>
      <KBottomAppBar>
        <Toolbar disableGutters>
          <Box sx={{ flexGrow: 1 }} />
          <BottomBarActionButton onClick={handleClose}>
            {t('Close')}
          </BottomBarActionButton>
        </Toolbar>
      </KBottomAppBar>
    </Stack>
  )
}
